import type {
  DataDatabase,
  FieldAppointmentEntry,
  OutboxEntry,
  PassageNoticeEntry,
} from '../../offline/data-db'

/**
 * Une ligne du fil d'activité de « Notifications » (#120, frame Terrain 2.7).
 *
 * ⚠️ **Lu dans les files d'envoi, pas dans une table de notifications** : chaque geste qu'elles
 * portent — contrôle transmis, avis de passage, rendez-vous pris sur place — y reste à vie, avec
 * son état. Une seconde table recopierait ce qu'elles savent déjà, et pourrait les contredire.
 * Seule la lecture s'y ajoute, en `readAt` non indexé.
 */
export type Activity = (
  | { kind: 'submission'; entry: OutboxEntry }
  | { kind: 'passage'; entry: PassageNoticeEntry }
  | { kind: 'appointment'; entry: FieldAppointmentEntry }
) & {
  /** L'heure du départ quand l'envoi est parti, sinon sa mise en file. */
  at: string
  unread: boolean
}

type Queued = OutboxEntry | PassageNoticeEntry | FieldAppointmentEntry

function itemOf<K extends Activity['kind']>(
  kind: K,
  entry: Extract<Activity, { kind: K }>['entry'],
): Activity {
  return {
    kind,
    entry,
    at: entry.sentAt ?? entry.createdAt,
    unread: entry.readAt == null,
  } as Activity
}

/** Le plus récent en tête : c'est le geste que l'agent vient de faire qu'il vient vérifier. */
export function activityOf(
  outbox: readonly OutboxEntry[],
  notices: readonly PassageNoticeEntry[],
  appointments: readonly FieldAppointmentEntry[],
): Activity[] {
  return [
    ...outbox.map((entry) => itemOf('submission', entry)),
    ...notices.map((entry) => itemOf('passage', entry)),
    ...appointments.map((entry) => itemOf('appointment', entry)),
  ].toSorted((left, right) => right.at.localeCompare(left.at))
}

function tableOf(data: DataDatabase, kind: Activity['kind']) {
  return { submission: data.outbox, passage: data.passageNotices, appointment: data.appointments }[
    kind
  ]
}

/**
 * ⚠️ Un `update` partiel, jamais un `put` : la passe d'envoi écrit l'état de la même ligne, et
 * une ligne entière relue plus tôt effacerait son départ. Une ligne disparue n'a rien à marquer.
 */
export async function markRead(data: DataDatabase, item: Activity, at: string): Promise<void> {
  await tableOf(data, item.kind).update(item.entry.id, { readAt: at })
}

/** « Tout marquer comme lu » — sans redater ce qui l'était déjà. */
export async function markAllRead(data: DataDatabase, at: string): Promise<void> {
  const unread = (row: Queued) => row.readAt == null
  await data.transaction('rw', [data.outbox, data.passageNotices, data.appointments], () =>
    Promise.all([
      data.outbox.filter(unread).modify({ readAt: at }),
      data.passageNotices.filter(unread).modify({ readAt: at }),
      data.appointments.filter(unread).modify({ readAt: at }),
    ]),
  )
}
